import { cn } from "@/lib/utils";

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

export function relativeTime(then: Date, now: Date = new Date()): string {
  const seconds = Math.round((then.getTime() - now.getTime()) / 1000);
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

function absolute(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  }).format(date);
}

/** A moment in time. Event times pass the event's `timeZone` and say so ("5:00 PM MDT"); system
 * times use the viewer's zone. The tooltip always gives the other one. */
export function Timestamp({
  value,
  timeZone,
  relative = true,
  className,
}: {
  value: string | Date | null | undefined;
  timeZone?: string | null;
  relative?: boolean;
  className?: string;
}) {
  if (!value) return <span className={cn("text-muted-foreground", className)}>None</span>;
  const date = value instanceof Date ? value : new Date(value);
  const tz = timeZone ?? undefined;
  const shown = absolute(date, tz);
  const other = tz ? absolute(date) : absolute(date, "UTC");
  return (
    <time
      dateTime={date.toISOString()}
      title={tz ? `${shown} (your time: ${other})` : `${shown} (UTC: ${other})`}
      className={cn("tabular", className)}
    >
      {shown}
      {relative && <span className="text-muted-foreground"> · {relativeTime(date)}</span>}
    </time>
  );
}
